/**
 * Secure Storage Service
 *
 * Intelligently routes storage based on data sensitivity:
 * - Sensitive data (tokens, secrets) → Secure Cookies
 * - Non-sensitive data (user metadata, preferences) → localStorage
 *
 * This provides defense-in-depth against XSS attacks while maintaining
 * performance benefits of localStorage for non-sensitive data.
 */

import CookieStorageService from "./cookie-storage";
import LocalStorageService from "./storage";

/**
 * Keys that contain sensitive data and should be stored in cookies
 */
const SENSITIVE_KEYS = [
  "adora-auth-token",
  "adora-refresh-token",
  "adora-media-auth-token",
  "adora-media-refresh-token",
  "adora-internal-auth-token",
  "adora-internal-refresh-token"
];

/**
 * Check if a key should be stored in cookies (sensitive) or localStorage
 */
const isSensitiveKey = (key: string): boolean => {
  return SENSITIVE_KEYS.some((sensitiveKey) => key.includes(sensitiveKey) || key.includes("token"));
};

/**
 * Migration: Check if data exists in localStorage and migrate to cookies
 */
const migrateFromLocalStorage = (key: string): void => {
  if (isSensitiveKey(key)) {
    const localStorageValue = LocalStorageService.getItem(key);
    if (localStorageValue) {
      // Migrate to cookie
      CookieStorageService.setItem(key, localStorageValue);
      // Remove from localStorage
      LocalStorageService.removeItem(key);
    }
  }
};

class SecureStorageService {
  /**
   * Store an item securely based on its sensitivity
   */
  setItem(key: string, value: string): void {
    if (isSensitiveKey(key)) {
      const useSecureCookie = typeof window !== "undefined" && window.location.protocol === "https:";
      // Store sensitive data in cookies
      CookieStorageService.setItem(key, value, {
        maxAge: 7, // 7 days
        secure: useSecureCookie,
        sameSite: "Strict"
      });
    } else {
      // Store non-sensitive data in localStorage for better performance
      LocalStorageService.setItem(key, value);
    }
  }

  /**
   * Retrieve an item, checking cookies first for sensitive data, then localStorage
   */
  getItem(key: string): string | null {
    // Attempt migration for existing users
    migrateFromLocalStorage(key);

    if (isSensitiveKey(key)) {
      // Check cookies for sensitive data
      return CookieStorageService.getItem(key);
    } else {
      // Check localStorage for non-sensitive data
      return LocalStorageService.getItem(key);
    }
  }

  /**
   * Remove an item from both storage types
   */
  removeItem(key: string): void {
    if (isSensitiveKey(key)) {
      CookieStorageService.removeItem(key);
    } else {
      LocalStorageService.removeItem(key);
    }
  }

  /**
   * Clear only auth token keys used for session credentials
   */
  clearAuthKeys(): void {
    SENSITIVE_KEYS.forEach((key) => {
      CookieStorageService.removeItem(key);
    });
  }

  /**
   * Clear all storage (both cookies and localStorage)
   */
  clear(): void {
    this.clearAuthKeys();

    // Clear localStorage
    LocalStorageService.clear();
  }

  /**
   * Check if an item exists in storage
   */
  hasItem(key: string): boolean {
    return this.getItem(key) !== null;
  }
}

export default new SecureStorageService();
